import { useEffect, useMemo, useState } from "react";
import { ImagePlus, LogIn, LogOut, Pencil, RefreshCw, RotateCcw, Trophy, UserRound } from "lucide-react";
import { progressSummary } from "../../engine";
import {
  getSession,
  loginUrl,
  logout,
  recordEvent,
  SITE_KEY,
  syncProgress,
  updateUserAvatar,
  type BdfzSession,
} from "../../lib/identity";
import {
  loadGlobalLeaderboard,
  loadLeaderboard,
  makeLeaderboardEntry,
  saveActivePlayerAvatar,
  saveActivePlayerName,
  relativeTime,
  clearAvatar,
  saveDisplayAlias,
  saveAvatar,
  splitLeaderboard,
  upsertLeaderboard,
  type GlobalLeaderboardEntry,
  type LeaderboardEntry,
} from "../../lib/leaderboard";
import { useGame } from "../../state/GameProvider";
import { DingMark } from "./DingMark";

const EXAM_TITLES = ["狀元", "榜眼", "探花", "傳臚"];

function examTitle(index: number): string {
  return EXAM_TITLES[index] || "進士";
}

// rank tier class — m1 狀元 … m5 進士+ (drives the 鼎 glow / rank-tag tint)
function medalClass(index: number): string {
  return `m${Math.min(index + 1, 5)}`;
}

function challengeText(
  current: LeaderboardEntry,
  champion?: LeaderboardEntry | GlobalLeaderboardEntry,
): string {
  if (!champion || champion.gates <= 0) {
    return "先破一門即可題名，榜上空席正等新科狀元。";
  }

  const gap = champion.gates - current.gates;
  if (gap <= 0) {
    return "你已不輸狀元榜首；再破一門，把榜眼探花甩在身後。";
  }

  const push = Math.max(1, Math.min(gap, 8));
  return `距狀元 ${champion.name} 還差 ${gap} 門；先連破 ${push} 門，直取三鼎甲。`;
}

function LeaderRows({
  title,
  rows,
  currentId,
}: {
  title: string;
  rows: Array<LeaderboardEntry | GlobalLeaderboardEntry>;
  currentId: string;
}) {
  return (
    <div className="leader-block">
      <div className="leader-head">{title}</div>
      {rows.length ? (
        rows.map((row, index) => (
          <div
            key={row.id}
            className={`leader-row ${medalClass(index)}${row.id === currentId ? " self" : ""}`}
          >
            <span className="leader-medal" title={examTitle(index)}>
              <DingMark rank={index} />
            </span>
            <span className="leader-id">
              <span className="leader-name">{row.name}</span>
              <span className="leader-rank-tag">{examTitle(index)}</span>
            </span>
            <span className="leader-score">
              {row.gates}
              <em>門</em>
            </span>
          </div>
        ))
      ) : (
        <div className="leader-empty">榜上空席 · 待君題名</div>
      )}
    </div>
  );
}

function readAvatarFile(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    if (!file.type.startsWith("image/")) {
      reject(new Error("not an image"));
      return;
    }

    const reader = new FileReader();
    reader.onerror = () => reject(new Error("file read failed"));
    reader.onload = () => {
      const image = new Image();
      image.onerror = () => reject(new Error("image decode failed"));
      image.onload = () => {
        const canvas = document.createElement("canvas");
        const size = 128;
        canvas.width = size;
        canvas.height = size;
        const context = canvas.getContext("2d");
        if (!context) {
          reject(new Error("canvas unavailable"));
          return;
        }
        context.clearRect(0, 0, size, size);
        const scale = Math.max(size / image.width, size / image.height);
        const width = image.width * scale;
        const height = image.height * scale;
        context.drawImage(image, (size - width) / 2, (size - height) / 2, width, height);
        resolve(canvas.toDataURL("image/webp", 0.82));
      };
      image.src = String(reader.result || "");
    };
    reader.readAsDataURL(file);
  });
}

export function UserPanel() {
  const { state } = useGame();
  const summary = progressSummary(state.progress);
  const [session, setSession] = useState<BdfzSession | null>(null);
  const [loading, setLoading] = useState(true);
  const [entries, setEntries] = useState<LeaderboardEntry[]>(() => loadLeaderboard());
  const [globalEntries, setGlobalEntries] = useState<GlobalLeaderboardEntry[]>([]);
  const [nameDraft, setNameDraft] = useState("");
  const [avatarDraft, setAvatarDraft] = useState("");
  const [profileVersion, setProfileVersion] = useState(0);

  const current = useMemo(
    () => makeLeaderboardEntry(session, summary, state.progress),
    [
      session?.authenticated,
      session?.user?.slug,
      summary.gates,
      summary.mastered,
      summary.percent,
      state.progress.bestStreak,
      state.progress.focus,
      profileVersion,
    ],
  );
  const boards = splitLeaderboard(entries);
  const globalRows = globalEntries.length ? globalEntries : boards.users;
  const champion = globalRows[0];
  const pushText = challengeText(current, champion);
  const displayUser = session?.authenticated && session.user ? session.user : null;
  const userName = displayUser?.display_name || displayUser?.displayName || displayUser?.slug || current.name;
  const avatar = current.avatar || displayUser?.avatar_url || displayUser?.avatarUrl || "";

  useEffect(() => {
    setNameDraft(current.name);
    setAvatarDraft(current.avatar?.startsWith("data:image/") ? "" : current.avatar || "");
    saveActivePlayerName(current.name);
    saveActivePlayerAvatar(current.avatar || "");
  }, [current.id, current.name, current.avatar]);

  async function refresh() {
    setLoading(true);
    const next = await getSession();
    setSession(next.authenticated ? next : { authenticated: false, user: null });
    setLoading(false);
    loadGlobalLeaderboard(8).then(setGlobalEntries);
  }

  async function handleLogout() {
    await logout();
    await refresh();
  }

  async function saveCurrentProfile(nextAvatar?: string) {
    const saved = saveDisplayAlias(current.id, nameDraft);
    const savedAvatar = saveAvatar(current.id, (nextAvatar ?? avatarDraft) || current.avatar || "");
    let nextSession = session;
    if (session?.authenticated && savedAvatar !== (displayUser?.avatar_url || displayUser?.avatarUrl || "")) {
      try {
        const updatedUser = await updateUserAvatar(savedAvatar);
        if (updatedUser) {
          nextSession = { authenticated: true, user: updatedUser };
          setSession(nextSession);
        }
      } catch {
        /* local avatar remains available if the user-center update fails */
      }
    }
    const next = {
      ...makeLeaderboardEntry(nextSession, summary, state.progress),
      name: saved || current.name,
      avatar: savedAvatar || undefined,
      updatedAt: Date.now(),
    };
    setEntries(upsertLeaderboard(next));
    setNameDraft(next.name);
    setAvatarDraft(savedAvatar.startsWith("data:image/") ? "" : savedAvatar);
    saveActivePlayerName(next.name);
    setProfileVersion((version) => version + 1);
    loadGlobalLeaderboard(8).then(setGlobalEntries);
  }

  function handleSaveName() {
    saveCurrentProfile().catch(() => {});
  }

  async function handleAvatarUpload(event: React.ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    try {
      const nextAvatar = await readAvatarFile(file);
      setAvatarDraft(nextAvatar);
      await saveCurrentProfile(nextAvatar);
    } catch {
      /* keep the existing avatar */
    }
  }

  async function handleClearAvatar() {
    clearAvatar(current.id);
    let nextSession = session;
    if (session?.authenticated) {
      try {
        const updatedUser = await updateUserAvatar("");
        if (updatedUser) {
          nextSession = { authenticated: true, user: updatedUser };
          setSession(nextSession);
        }
      } catch {
        /* local clear still applies if the user-center update fails */
      }
    }
    const next = { ...makeLeaderboardEntry(nextSession, summary, state.progress), avatar: undefined, updatedAt: Date.now() };
    setAvatarDraft("");
    setEntries(upsertLeaderboard(next));
    setProfileVersion((version) => version + 1);
    loadGlobalLeaderboard(8).then(setGlobalEntries);
  }

  useEffect(() => {
    refresh();
    window.addEventListener("focus", refresh);
    return () => window.removeEventListener("focus", refresh);
  }, []);

  useEffect(() => {
    const nextEntries = upsertLeaderboard(current);
    setEntries(nextEntries);

    const completed = summary.gates >= summary.totalGates;
    const syncTimer = window.setTimeout(() => {
      if (!session?.authenticated) return;

      syncProgress({
        siteKey: SITE_KEY,
        itemKey: "lunyu-total",
        itemTitle: "義戰論語總進度",
        itemGroup: "論語",
        itemType: "battle-game",
        state: completed ? "done" : "in_progress",
        completed: true,
        score: summary.gates,
        progressPercent: summary.percent,
        meta: {
          mastered: summary.mastered,
          total: summary.total,
          gates: summary.gates,
          totalGates: summary.totalGates,
          bestStreak: state.progress.bestStreak,
          dayStreak: state.progress.dayStreak,
          bestDayStreak: state.progress.bestDayStreak,
          focus: state.progress.focus,
          rankKind: current.kind,
          displayAlias: current.name,
          avatarOverride: current.avatar || "",
        },
      }).catch(() => {});

      recordEvent({
        siteKey: SITE_KEY,
        kind: "event",
        recordKey: "lunyu-leaderboard-current",
        title: "義戰論語排行榜快照",
        summary: `${summary.gates}/${summary.totalGates} 門 · ${summary.mastered}/${summary.total} 則`,
        itemGroup: "leaderboard",
        itemType: "progress-snapshot",
        payload: {
          gates: summary.gates,
          totalGates: summary.totalGates,
          mastered: summary.mastered,
          percent: summary.percent,
          bestStreak: state.progress.bestStreak,
          dayStreak: state.progress.dayStreak,
          bestDayStreak: state.progress.bestDayStreak,
          focus: state.progress.focus,
          displayAlias: current.name,
          avatarOverride: current.avatar || "",
        },
      }).catch(() => {});
    }, 900);

    return () => window.clearTimeout(syncTimer);
  }, [
    current.id,
    current.kind,
    current.name,
    current.avatar,
    session?.authenticated,
    summary.gates,
    summary.mastered,
    summary.percent,
    summary.total,
    summary.totalGates,
    state.progress.bestStreak,
    state.progress.focus,
  ]);

  return (
    <section className="panel user-panel">
      <div className="panel-title">
        <UserRound size={15} />
        Seiue 用戶
        <strong>{displayUser ? "已連接" : "訪客"}</strong>
      </div>

      <div className="user-card">
        <span className="user-avatar">
          {avatar ? <img src={avatar} alt="" /> : <UserRound size={18} />}
        </span>
        <span className="user-meta">
          <strong>{loading ? "檢查中" : userName}</strong>
          <small>{displayUser ? `@${displayUser.slug}` : "可不登錄遊玩；登錄後同步總進度"}</small>
        </span>
      </div>

      <div className="user-actions">
        {displayUser ? (
          <button type="button" className="mini-btn" onClick={handleLogout}>
            <LogOut size={13} />
            退出
          </button>
        ) : (
          <a className="mini-btn primary" href={loginUrl()}>
            <LogIn size={13} />
            Seiue 登錄
          </a>
        )}
        <button type="button" className="mini-btn" onClick={refresh}>
          <RefreshCw size={13} />
          刷新
        </button>
        <span className="sync-note">{relativeTime(current.updatedAt)}</span>
      </div>

      <div className="alias-row">
        <label htmlFor="display-alias">遊戲名</label>
        <input
          id="display-alias"
          value={nameDraft}
          maxLength={24}
          onChange={(event) => setNameDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") handleSaveName();
          }}
        />
        <button type="button" className="mini-icon" onClick={handleSaveName} title="保存遊戲名">
          <Pencil size={12} />
        </button>
      </div>

      <div className="alias-row avatar-row">
        <label htmlFor="display-avatar">頭像</label>
        <input
          id="display-avatar"
          value={avatarDraft}
          placeholder="圖片 URL"
          onChange={(event) => setAvatarDraft(event.target.value)}
          onKeyDown={(event) => {
            if (event.key === "Enter") saveCurrentProfile().catch(() => {});
          }}
        />
        <button type="button" className="mini-icon" onClick={() => saveCurrentProfile().catch(() => {})} title="保存頭像">
          <Pencil size={12} />
        </button>
      </div>
      <div className="avatar-tools">
        <label className="mini-btn">
          <ImagePlus size={13} />
          上傳
          <input type="file" accept="image/*" onChange={handleAvatarUpload} />
        </label>
        <button type="button" className="mini-btn" onClick={() => handleClearAvatar().catch(() => {})}>
          <RotateCcw size={13} />
          清除
        </button>
      </div>

      <div className="leader-title">
        <Trophy size={14} />
        科舉榜
      </div>
      <div className="leader-push">{pushText}</div>
      <div className="leader-grid">
        <LeaderRows title="全站登錄" rows={globalRows} currentId={`global:${displayUser?.slug}`} />
      </div>
    </section>
  );
}
